import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { FaArrowRight, FaGlobe } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useAnimationSettings from "../../hooks/useAnimationSettings";

const VIOLETA = "#697B68";
const ROSA = "#E09767";
const TYR_SITE = "https://www.tyr-ai.com.ar";

const pillars = [
  {
    title: "Entender el proceso",
    description:
      "Antes de implementar tecnología, analizamos cómo trabaja cada equipo y donde están las verdaderas fricciones.",
  },
  {
    title: "Absorber complejidad",
    description:
      "Nos encargamos de la capa técnica para que el usuario final se encuentre con soluciones claras y usables.",
  },
  {
    title: "Impacto real",
    description:
      "Software, automatización e IA aplicadas con foco en mejorar operaciones, decisiones y resultados concretos.",
  },
];

const quickPoints = [
  "Empresa creada para sistematizar una metodología de trabajo",
  "Software, automatización e inteligencia artificial aplicada",
  "La tecnología debe adaptarse a las personas, no al reves",
];

function getFadeIn(animation) {
  return {
    initial: animation.reduceMotion
      ? false
      : { opacity: 0, y: animation.offsets.revealY },
    whileInView: { opacity: 1, y: 0 },
    viewport: animation.viewport,
    transition: {
      duration: animation.reduceMotion ? 0 : animation.durations.reveal,
      ease: "easeOut",
    },
  };
}

export default function Tyr() {
  const navigate = useNavigate();
  const animation = useAnimationSettings();
  const fadeIn = getFadeIn(animation);
  const {
    reduceMotion,
    compactMotion,
    allowHoverAnimations,
    durations,
    offsets,
    viewport,
  } = animation;

  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo(0, 0);
    if (typeof document !== "undefined") {
      document.title = "TYR | Sebas Sotelo | Soluciones, productos e IA aplicada";
    }
  }, []);

  const backgroundStyle = {
    backgroundColor: "#EADFD2",
    backgroundImage: "linear-gradient(180deg, rgba(248,242,234,0.95) 0%, rgba(234,223,210,0.96) 58%, rgba(248,242,234,1) 100%)",
  };

  return (
    <div className="relative min-h-screen w-full" style={backgroundStyle}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(90deg, #697B68 1px, transparent 1px), linear-gradient(#697B68 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
      </div>

      <section className="page-hero relative flex items-center overflow-hidden lg:min-h-screen">
        <div className="page-container relative">
          <div className="grid items-center gap-12 lg:grid-cols-[1.2fr,0.8fr] lg:gap-16">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: offsets.revealY }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: reduceMotion ? 0 : durations.reveal,
                ease: "easeOut",
              }}
              className="motion-gpu space-y-5 text-center md:space-y-8 lg:text-left"
            >
              <div className="space-y-4">
                <motion.div
                  initial={reduceMotion ? false : { opacity: 0, x: -offsets.sideX }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: reduceMotion ? 0 : durations.reveal,
                    delay: reduceMotion ? 0 : compactMotion ? 0.06 : 0.1,
                  }}
                  className="motion-gpu inline-block"
                >
                  <span
                    className="mx-auto inline-flex text-xs font-semibold tracking-wider uppercase px-3 py-2 rounded-full sm:text-sm sm:px-4 lg:mx-0"
                    style={{ backgroundColor: `${ROSA}1A`, color: ROSA }}
                  >
                    TYR
                  </span>
                </motion.div>

                <h1 className="space-y-4">
                  <span
                    className="hero-name block tracking-tight"
                    style={{ color: VIOLETA }}
                  >
                    Tecnología
                  </span>
                  <span
                    className="section-title block"
                    style={{
                      background: `linear-gradient(135deg, ${ROSA} 0%, ${VIOLETA} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    que se adapta a las personas
                  </span>
                </h1>
              </div>

              <div className="mx-auto max-w-sm space-y-4 sm:max-w-2xl lg:mx-0">
                <p
                  className="body-copy"
                  style={{ color: `${VIOLETA}CC` }}
                >
                  TYR nace para sistematizar un método de trabajo: absorber la complejidad técnica y convertir software, automatización e inteligencia artificial en soluciones simples de usar y enfocadas en resultados.
                </p>

                <div className="hidden gap-3 sm:grid">
                  {quickPoints.map((point) => (
                    <div key={point} className="flex items-start gap-3">
                      <span
                        className="mt-[10px] h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: ROSA }}
                      />
                      <p
                        className="text-base leading-relaxed"
                        style={{ color: `${VIOLETA}B8` }}
                      >
                        {point}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex w-full flex-col items-stretch gap-3 pt-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                <a
                  href={TYR_SITE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group motion-gpu relative inline-flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-3.5 font-semibold text-white shadow-lg transition-all duration-300 overflow-hidden sm:w-auto sm:px-8 sm:py-4 ${
                    allowHoverAnimations ? "hover:shadow-xl" : ""
                  }`}
                  style={{ backgroundColor: VIOLETA }}
                >
                  <span className="relative z-10 inline-flex items-center gap-2">
                    <FaGlobe className="text-sm" />
                    Visitar tyr-ai.com.ar
                  </span>
                  <div
                    className={`absolute inset-0 opacity-0 transition-opacity duration-300 ${
                      allowHoverAnimations ? "group-hover:opacity-100" : ""
                    }`}
                    style={{
                      background: `linear-gradient(135deg, ${VIOLETA} 0%, ${ROSA} 100%)`,
                    }}
                  />
                </a>

                <button
                  onClick={() => navigate("/sobre-mi#portfolio")}
                  className={`motion-gpu w-full rounded-2xl border-2 px-6 py-3.5 font-semibold transition-all duration-300 sm:w-auto sm:px-8 sm:py-4 ${
                    allowHoverAnimations ? "hover:shadow-lg" : ""
                  }`}
                  style={{
                    borderColor: VIOLETA,
                    color: VIOLETA,
                    backgroundColor: "rgba(255,255,255,0.9)",
                  }}
                >
                  Ver proyectos
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={reduceMotion ? false : { opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: reduceMotion ? 0 : durations.hero,
                delay: reduceMotion ? 0 : compactMotion ? 0.12 : 0.2,
                ease: "easeOut",
              }}
              className="motion-gpu relative mx-auto hidden w-full max-w-[280px] sm:block sm:max-w-lg"
            >
              <div className="relative w-full max-w-lg">
                <div
                  className="absolute inset-0 rounded-[2.5rem] blur-2xl opacity-40"
                  style={{
                    background: `linear-gradient(135deg, ${ROSA} 0%, ${VIOLETA} 100%)`,
                  }}
                />
                <div
                  className="relative rounded-[2.5rem] p-8 shadow-2xl"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.9)",
                    border: `1px solid ${VIOLETA}1A`,
                  }}
                >
                  <div className="space-y-6 text-center">
                    <div className="flex justify-center">
                      <img
                        src="/TYR logo negro.png"
                        alt="TYR"
                        className="h-24 w-auto object-contain sm:h-28"
                      />
                    </div>

                    <div className="space-y-3">
                      <p
                        className="text-xs font-semibold uppercase tracking-[0.24em]"
                        style={{ color: ROSA }}
                      >
                        tyr-ai.com.ar
                      </p>
                      <p
                        className="text-lg font-bold"
                        style={{ color: VIOLETA }}
                      >
                        Una empresa creada para empaquetar una metodología y llevar soluciones técnicas complejas a experiencias simples para los equipos.
                      </p>
                    </div>

                    <a
                      href={TYR_SITE}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold underline underline-offset-4"
                      style={{ color: VIOLETA }}
                    >
                      www.tyr-ai.com.ar
                      <FaArrowRight className="text-xs" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="portfolio-section relative">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeIn} className="mb-10 text-center md:mb-16">
            <h2
              className="section-title mb-4"
              style={{ color: VIOLETA }}
            >
              Cómo nace TYR
            </h2>
            <div
              className="w-20 h-1 mx-auto rounded-full"
              style={{
                background: `linear-gradient(135deg, ${ROSA} 0%, ${VIOLETA} 100%)`,
              }}
            />
          </motion.div>

          <motion.div
            {...fadeIn}
            className="mx-auto max-w-4xl rounded-2xl p-5 shadow-md sm:rounded-[2rem] sm:p-10 sm:shadow-xl"
            style={{
              backgroundColor: "rgba(255,255,255,0.9)",
              border: `1px solid ${VIOLETA}1A`,
            }}
          >
            <div className="space-y-4 text-left sm:space-y-5">
              <p className="body-copy" style={{ color: `${VIOLETA}CC` }}>
                Hace tiempo vengo ayudando a empresas a optimizar sus procesos, y en ese recorrido confirme algo fundamental: implementar inteligencia artificial o desarrollar software a medida no es una tarea sencilla, pero usar esas herramientas en el día a día sí deberia serlo.
              </p>
              <p className="body-copy" style={{ color: `${VIOLETA}BF` }}>
                Muchas organizaciones incorporan nuevas herramientas o intentan adoptar inteligencia artificial solo por seguir una tendencia. El problema aparece cuando esos sistemas se implementan sin entender el proceso de fondo y terminan sumando complejidad en lugar de facilitar el trabajo.
              </p>
              <p className="body-copy" style={{ color: `${VIOLETA}BF` }}>
                Ahí es donde entra TYR: absorbemos esa complejidad técnica para que los equipos solo experimenten soluciones. Con el objetivo de empaquetar esta metodología y sistematizar desarrollos que generen impacto real, sin importar el nivel técnico de quien los use, decidimos darle nombre a este enfoque.
              </p>
              <p className="text-lg leading-relaxed font-semibold" style={{ color: VIOLETA }}>
                Así nace TYR.
              </p>
            </div>
          </motion.div>

          <div className="pillar-grid mt-12">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={reduceMotion ? false : { opacity: 0, y: offsets.revealY }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewport}
                transition={{
                  duration: reduceMotion ? 0 : durations.reveal * 0.6,
                  delay: reduceMotion ? 0 : index * durations.stagger,
                }}
                className={`group motion-gpu relative overflow-hidden rounded-3xl p-8 shadow-lg transition-all duration-500 ${
                  allowHoverAnimations ? "hover:shadow-2xl" : ""
                }`}
                style={{
                  backgroundColor: "rgba(255,255,255,0.9)",
                  border: `1px solid ${VIOLETA}1A`,
                }}
              >
                <div className="space-y-4 relative z-10">
                  <h3 className="card-title" style={{ color: VIOLETA }}>
                    {pillar.title}
                  </h3>
                  <p
                    className="text-base leading-relaxed"
                    style={{ color: `${VIOLETA}99` }}
                  >
                    {pillar.description}
                  </p>
                </div>
                <div
                  className={`absolute bottom-0 left-0 right-0 h-[4px] w-0 transition-all duration-500 ${
                    allowHoverAnimations ? "group-hover:w-full" : ""
                  }`}
                  style={{
                    background: `linear-gradient(135deg, ${ROSA} 0%, ${VIOLETA} 100%)`,
                    borderBottomLeftRadius: "1.5rem",
                    borderBottomRightRadius: "1.5rem",
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="portfolio-section relative">
        <div className="mx-auto max-w-5xl">
          <motion.div
            {...fadeIn}
            className="relative overflow-hidden rounded-[2.25rem] p-[1px] shadow-2xl"
            style={{
              background: `linear-gradient(135deg, ${ROSA}, ${VIOLETA})`,
              boxShadow:
                "0 12px 40px rgba(105,123,104,0.18), 0 4px 12px rgba(105,123,104,0.10)",
            }}
          >
            <div
              className="card-padding relative rounded-[2.1rem] bg-white/90 backdrop-blur"
              style={{ border: `1px solid ${VIOLETA}1A` }}
            >
              <div className="relative space-y-4 text-center sm:space-y-6">
                <h3
                  className="section-title"
                  style={{ color: VIOLETA }}
                >
                  Una nueva etapa
                </h3>
                <p
                  className="body-copy mx-auto max-w-2xl"
                  style={{ color: `${VIOLETA}BF` }}
                >
                  A traves de este espacio, la idea es mostrar como nuestros clientes ya vienen trabajando y logrando sus objetivos, demostrando con casos reales que cuando nosotros resolvemos la tecnología de fondo, la mejora para el usuario final se vuelve natural.
                </p>

                <button
                  onClick={() => navigate("/sobre-mi")}
                  className={`group motion-gpu relative inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 ${
                    allowHoverAnimations ? "hover:shadow-xl" : ""
                  }`}
                  style={{ backgroundColor: VIOLETA }}
                >
                  Conocer mi perfil
                  <motion.span
                    initial={{ x: 0 }}
                    whileHover={allowHoverAnimations ? { x: 4 } : undefined}
                    transition={{ type: "spring", stiffness: 400, damping: 28 }}
                  >
                    <FaArrowRight className="text-sm" />
                  </motion.span>
                  <span
                    className={`pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 ${
                      allowHoverAnimations ? "group-hover:opacity-100" : ""
                    }`}
                    style={{
                      background: `linear-gradient(135deg, ${VIOLETA}, ${ROSA})`,
                      mixBlendMode: "overlay",
                    }}
                  />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
